/**
 * i18next setup of the admin app (SPEC-FRONTEND §3).
 *
 * Every namespace of both languages is imported statically here (phase 01) — this file is frozen
 * afterwards: feature agents only edit the JSON files of their own namespaces. `common` and
 * `errors` are shared and frozen too. `scripts/check-i18n.mjs` (part of `npm run lint`) verifies
 * ru/en key parity and that every locale file is registered below.
 */
import dayjs from 'dayjs';
import 'dayjs/locale/ru';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

import enAudit from './locales/en/audit.json';
import enAuth from './locales/en/auth.json';
import enCommon from './locales/en/common.json';
import enComplaints from './locales/en/complaints.json';
import enDashboard from './locales/en/dashboard.json';
import enDeals from './locales/en/deals.json';
import enErrors from './locales/en/errors.json';
import enNav from './locales/en/nav.json';
import enOutbox from './locales/en/outbox.json';
import enSettings from './locales/en/settings.json';
import enShipments from './locales/en/shipments.json';
import enUsers from './locales/en/users.json';
import enVerification from './locales/en/verification.json';
import ruAudit from './locales/ru/audit.json';
import ruAuth from './locales/ru/auth.json';
import ruCommon from './locales/ru/common.json';
import ruComplaints from './locales/ru/complaints.json';
import ruDashboard from './locales/ru/dashboard.json';
import ruDeals from './locales/ru/deals.json';
import ruErrors from './locales/ru/errors.json';
import ruNav from './locales/ru/nav.json';
import ruOutbox from './locales/ru/outbox.json';
import ruSettings from './locales/ru/settings.json';
import ruShipments from './locales/ru/shipments.json';
import ruUsers from './locales/ru/users.json';
import ruVerification from './locales/ru/verification.json';

export const LANGUAGES = ['ru', 'en'] as const;
export type AppLanguage = (typeof LANGUAGES)[number];
export const DEFAULT_LANGUAGE: AppLanguage = 'ru';

export const NAMESPACES = [
  'common',
  'errors',
  'nav',
  'auth',
  'dashboard',
  'users',
  'verification',
  'shipments',
  'deals',
  'complaints',
  'settings',
  'outbox',
  'audit',
] as const;
export type AppNamespace = (typeof NAMESPACES)[number];

/** `ru` is the reference language: its JSON shapes type every `t()` call (see `i18next.d.ts`). */
export const resources = {
  ru: {
    common: ruCommon,
    errors: ruErrors,
    nav: ruNav,
    auth: ruAuth,
    dashboard: ruDashboard,
    users: ruUsers,
    verification: ruVerification,
    shipments: ruShipments,
    deals: ruDeals,
    complaints: ruComplaints,
    settings: ruSettings,
    outbox: ruOutbox,
    audit: ruAudit,
  },
  en: {
    common: enCommon,
    errors: enErrors,
    nav: enNav,
    auth: enAuth,
    dashboard: enDashboard,
    users: enUsers,
    verification: enVerification,
    shipments: enShipments,
    deals: enDeals,
    complaints: enComplaints,
    settings: enSettings,
    outbox: enOutbox,
    audit: enAudit,
  },
} satisfies Record<AppLanguage, Record<AppNamespace, object>>;

/** The admin UI language is a per-browser preference (the admin profile has no language field). */
const LANGUAGE_STORAGE_KEY = 'adm_lang';

export function isAppLanguage(value: unknown): value is AppLanguage {
  return typeof value === 'string' && (LANGUAGES as readonly string[]).includes(value);
}

function readStoredLanguage(): AppLanguage | null {
  try {
    const stored = window.localStorage.getItem(LANGUAGE_STORAGE_KEY);
    return isAppLanguage(stored) ? stored : null;
  } catch {
    return null;
  }
}

function detectLanguage(): AppLanguage {
  const browser = navigator.language?.slice(0, 2).toLowerCase();
  return readStoredLanguage() ?? (isAppLanguage(browser) ? browser : DEFAULT_LANGUAGE);
}

/** Side effects that must follow the UI language: `<html lang>` and dayjs month/day names. */
function applyLanguage(language: AppLanguage): void {
  document.documentElement.lang = language;
  dayjs.locale(language);
}

dayjs.extend(customParseFormat);

void i18n.use(initReactI18next).init({
  resources,
  lng: detectLanguage(),
  fallbackLng: DEFAULT_LANGUAGE,
  supportedLngs: LANGUAGES,
  ns: NAMESPACES,
  defaultNS: 'common',
  // Resources are bundled, so initialisation can complete synchronously (no Suspense, no flash).
  initAsync: false,
  // React escapes rendered strings itself; HTML in translations is forbidden by the lint script.
  interpolation: { escapeValue: false },
  react: { useSuspense: false },
  // No vendor banner in the admins' browser console.
  showSupportNotice: false,
});

applyLanguage(currentLanguage());

i18n.on('languageChanged', (language) => {
  const next = isAppLanguage(language) ? language : DEFAULT_LANGUAGE;
  applyLanguage(next);
  try {
    window.localStorage.setItem(LANGUAGE_STORAGE_KEY, next);
  } catch {
    // Storage may be unavailable (private mode); the choice then lasts for this tab only.
  }
});

/** Current UI language for non-React code (HTTP headers, formatters). */
export function currentLanguage(): AppLanguage {
  const language = i18n.resolvedLanguage ?? i18n.language;
  return isAppLanguage(language) ? language : DEFAULT_LANGUAGE;
}

export default i18n;
